import { useEffect, useState, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import { Button, Space, Table, Tag, Tooltip, Typography, Flex, Modal, message } from 'antd';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { laborProgressApi, exportLaborProgressApi } from '@services/Conference';
import { numberProject } from '@utils/utils';
import { conferenceTypes } from '../constants';
import { laborFeeProcessNodes } from '@utils/constants';
import PageHeader from '@components/PageHeader/PageHeader';
import LaborProgressFilterPanel from './LaborProgressFilterPanel';
import './index.less';
import { TABLE_PAGE_SIZE_OPTIONS } from '@/utils/property';
const { Text } = Typography;

export default function LaborProgress() {
    const [filter, setFilter] = useState({
        page: 1,
        pageSize: 10,
        isTesting: 0,
    });

    const [tableData, setTableData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const onFinish = values => {
        const newFilter = { ...filter, page: 1, ...values };
        setFilter(newFilter);
    };

    const onReset = () => {
        setFilter({ page: 1, pageSize: 10, isTesting: 0 });
    };

    const onChangePage = (page, pageSize) => {
        setFilter({
            ...filter,
            page,
            pageSize,
        });
    };

    const onShowSizeChange = (page, pageSize) => onChangePage(1, pageSize);

    const handleRefresh = () => getLaborProgress(filter);

    // 导出项目劳务进度
    const exportLaborProgress = async () => {
        if (exportLoading) return;

        try {
            setExportLoading(true);
            const params = { ...filter };
            delete params.page;
            delete params.pageSize;

            const response = await exportLaborProgressApi(params);
            const url = window.URL.createObjectURL(response);
            const link = document.createElement('a');
            link.href = url;
            link.download = `项目劳务进度${dayjs().format('YYYYMMDDHHmmss')}.xlsx`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('导出项目劳务进度失败:', error);
        } finally {
            setExportLoading(false);
        }
    };

    // 获取状态颜色
    const getStatusColor = (approvalStatus, nodeId) => {
        switch (approvalStatus) {
            case 2:
                if (nodeId === 7 || nodeId === 9) {
                    return '#FA8C16';
                }
                return '#52C41A';
            case 1:
                return '#FA8C16';
            case 3:
                if (nodeId === 7 || nodeId === 9) {
                    return '#52C41A';
                }
                if (nodeId === 10) {
                    return '#52C41A';
                }
                return '#FF4D4F';
            case 0:
            default:
                return '#B3B3B3';
        }
    };

    const columns = [
        {
            title: '项目名称',
            dataIndex: 'id',
            width: 300,
            render: (_, record) => {
                return (
                    <>
                        <a
                            onClick={() => {
                                window.location.href = `/conf/conferences/${record.conferenceId}/laborfee`;
                            }}
                            style={{ verticalAlign: 'middle' }}
                        >
                            {record.name}
                        </a>
                        {!!record.type && (
                            <Tag color="volcano" style={{ marginLeft: 4, marginRight: 0, padding: 2, fontSize: 10, lineHeight: '1em', verticalAlign: 'middle' }}>
                                {conferenceTypes.find(item => item.type === record.type)?.label}
                            </Tag>
                        )}
                        {!!record.isTesting && (
                            <Tag color="#f50" style={{ marginLeft: 4, marginRight: 0, padding: 2, fontSize: 10, lineHeight: '1em', verticalAlign: 'middle' }}>
                                TEST
                            </Tag>
                        )}
                        <div>氚云项目编号：{record.h3yunNo || <>&ndash;</>}</div>
                        <div>
                            <Text type="secondary">会议项目编号：{numberProject('YHY', record.conferenceId)}</Text>
                        </div>
                    </>
                );
            },
        },
        {
            title: '项目执行日期',
            dataIndex: 'dates',
            width: 200,
            render: (_, record) => {
                const dateList = record.dates?.split(/,/) || [];
                return dateList.join(', ');
            },
        },
        {
            title: '项目执行进度',
            dataIndex: 'conferenceStatus',
            width: 120,
            align: 'center',
            render: (_, record) => {
                return (
                    <>
                        {record.conferenceStatus === 0 && <Text type="secondary">未开始</Text>}
                        {record.conferenceStatus === 1 && <Text type="warning">进行中</Text>}
                        {record.conferenceStatus === 2 && <Text type="success">已完成</Text>}
                    </>
                );
            },
        },
        {
            title: '付款流程',
            dataIndex: 'flows',
            width: 1000,
            render: (_, record) => {
                const getDefaultFlows = () => {
                    return laborFeeProcessNodes.map((node, index) => ({
                        nodeId: node.nodeId,
                        nodeName: node.name,
                        approvalStatus: index === 0 ? 1 : 0,
                        approvalStatusName: index === 0 ? '待提交' : '未开始',
                    }));
                };

                const flows = record.flows && !!record.flows.length ? record.flows : getDefaultFlows();

                return (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', overflow: 'auto', whiteSpace: 'nowrap' }}>
                        {flows.map((item, index) => {
                            const statusColor = getStatusColor(item.approvalStatus, item.nodeId);
                            return (
                                <div
                                    key={item.nodeId}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        flexShrink: 0,
                                    }}
                                >
                                    <div
                                        style={{
                                            padding: '4px 8px',
                                            backgroundColor: 'rgba(248, 250, 252, 1)',
                                            border: '1px solid #d9d9d9',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <div>{item.nodeName}</div>
                                        <div style={{ color: statusColor, fontWeight: 600 }}>
                                            {item.approvalStatusName}
                                            {item.approvalByName && <span>({item.approvalByName})</span>}
                                        </div>
                                    </div>
                                    {index < flows.length - 1 && <span style={{ color: '#999', fontSize: '12px', flexShrink: 0 }}>→</span>}
                                </div>
                            );
                        })}
                    </div>
                );
            },
        },
        {
            title: '已付/应付人数',
            dataIndex: 'paidAndTotalNum',
            width: 140,
            align: 'center',
            render: (_, record) => {
                const paidExists = record.laborfeePaidNum !== undefined && record.laborfeePaidNum !== null;
                const totalExists = record.laborfeeTotalNum !== undefined && record.laborfeeTotalNum !== null;

                if (!paidExists && !totalExists) {
                    return <Text type="secondary">&ndash;</Text>;
                }

                const paidDisplay = paidExists ? <Text type="success">{record.laborfeePaidNum}</Text> : <Text type="secondary">&ndash;</Text>;
                const totalDisplay = totalExists ? <span>{record.laborfeeTotalNum}</span> : <Text type="secondary">&ndash;</Text>;

                return (
                    <span>
                        {paidDisplay} / {totalDisplay}
                    </span>
                );
            },
        },
        {
            title: '项目创建人',
            dataIndex: 'createdBy',
            width: 100,
            render: data => (!!data ? data : <Text type="secondary">&ndash;</Text>),
        },
    ];

    const getLaborProgress = async (currentFilter = filter) => {
        try {
            setLoading(true);

            const params = { ...currentFilter };
            if (params.ids && !Array.isArray(params.ids)) {
                try {
                    const idsStr = params.ids?.replace(/YHY0+/gi, '') || '';
                    const idsArray = idsStr.split(',') || [];
                    if (idsArray.length) {
                        params.ids = idsArray.map(id => parseInt(id) || 0).filter(id => id > 0);
                    } else {
                        params.ids = [];
                    }
                } catch (e) {
                    console.error('解析项目编号错误:', e);
                    params.ids = [];
                }
            }

            const res = await laborProgressApi(params);
            if (res.status_code === 200) {
                const page = params.page ?? 1;
                const list = res.data?.list ?? [];
                setTableData(
                    list.map((item, index) => ({
                        ...item,
                        __tableRowUid: `${page}-${index}`,
                    })),
                );
                setTotal(res.data?.totalCount ?? 0);
            }

            setLoading(false);
        } catch (e) {
            setLoading(false);
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: 依赖 filter 语义即可
    useEffect(() => {
        getLaborProgress();
    }, [filter]);

    useEffect(() => {
        document.title = '项目劳务进度-医会议';
    }, []);

    return (
        <div className="page-container labor-progress-page">
            <PageHeader title="项目劳务进度" breadcrumb={[{ label: '医会议', path: '/conf/conferences' }, { label: '项目劳务进度' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <LaborProgressFilterPanel filter={filter} onFinish={onFinish} onReset={onReset} />
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container labor-progress-table-region">
                <div className="main-toolbar labor-progress-toolbar">
                    <div className="main-tools">
                        <span className="table-title">项目列表</span>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="导出当前筛选结果">
                                <Button type="default" onClick={exportLaborProgress} loading={exportLoading} disabled={exportLoading} icon={<span className="bi bi-cloud-download" aria-hidden="true" />} aria-label="导出" />
                            </Tooltip>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={handleRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap labor-progress-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        rowKey={record => record.__tableRowUid}
                        columns={columns}
                        dataSource={tableData}
                        loading={loading}
                        bordered
                        sticky
                        size="medium"
                        scroll={{
                            x: 'max-content',
                            ...(tableScrollY != null ? { y: tableScrollY } : {}),
                        }}
                        pagination={{
                            total,
                            showTotal: () => `共${total}条`,
                            current: filter.page,
                            pageSize: filter.pageSize,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            showQuickJumper: true,
                            showSizeChanger: { placement: 'topRight' },
                            onChange: onChangePage,
                            onShowSizeChange,
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
